/**
 * Shimmer: the reverb's wet output through a pitch-up path (+12 or +7) fed back into the convolver.
 * Uses signalsmith-stretch's realtime worklet node when it loads, else our two-grain pitchshift worklet.
 */
import type { Drone } from '@tonist/shared';
import { loadStretch, withTimeout, type StretchNode } from '../stretch';
import { clamp, targetTau } from '../params';

type ShimmerSpec = Drone['space']['shimmer'];

export class Shimmer {
  /** feed with the reverb wet signal */
  readonly input: GainNode;
  /** connect to the reverb's convolver input */
  readonly feedback: GainNode;
  /** wet shimmer to the bus */
  readonly output: GainNode;
  private readonly hp: BiquadFilterNode;
  private readonly lp: BiquadFilterNode;
  private readonly shifted: GainNode;
  private readonly comp: DynamicsCompressorNode;
  private stretch: StretchNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private semitones: number;
  private disposed = false;
  readonly ready: Promise<void>;

  constructor(private readonly ctx: AudioContext, spec: ShimmerSpec, pitchshiftWorklet: boolean) {
    this.semitones = spec.interval;
    this.input = ctx.createGain();
    this.feedback = ctx.createGain();
    this.feedback.gain.value = 0;
    this.output = ctx.createGain();
    this.output.gain.value = 0;
    this.hp = ctx.createBiquadFilter();
    this.hp.type = 'highpass';
    this.hp.frequency.value = 200;
    this.lp = ctx.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 7000;
    this.shifted = ctx.createGain();
    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -12;
    this.comp.ratio.value = 8;
    this.comp.attack.value = 0.01;
    this.comp.release.value = 0.3;
    this.input.connect(this.hp);
    this.hp.connect(this.lp);
    this.shifted.connect(this.comp);
    this.comp.connect(this.feedback);
    this.comp.connect(this.output);
    this.ready = this.init(pitchshiftWorklet);
  }

  private async init(useWorklet: boolean): Promise<void> {
    try {
      const factory = await loadStretch();
      const node = await withTimeout(factory(this.ctx, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] }), 8000, 'shimmer stretch node');
      if (this.disposed) { try { node.disconnect(); } catch { /* ignore */ } return; }
      await node.start();
      await node.schedule({ semitones: this.semitones, tonalityHz: 8000 });
      this.lp.connect(node);
      node.connect(this.shifted);
      this.stretch = node;
    } catch (e) {
      if (this.disposed) return;
      if (useWorklet) {
        try {
          const w = new AudioWorkletNode(this.ctx, 'tonist-pitchshift', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] });
          const p = w.parameters.get('semitones');
          if (p) p.value = this.semitones;
          this.lp.connect(w);
          w.connect(this.shifted);
          this.worklet = w;
          console.warn('[tonist] shimmer: signalsmith-stretch unavailable, using the two-grain pitchshift worklet', e);
        } catch (e2) {
          console.warn('[tonist] shimmer unavailable', e2);
        }
      } else {
        console.warn('[tonist] shimmer unavailable (no stretch, no worklet)', e);
      }
    }
  }

  set(spec: ShimmerSpec, mix: number, t: number): void {
    targetTau(this.feedback.gain, clamp(spec.feedback, 0, 1) * 0.75, t, 0.2);
    targetTau(this.output.gain, clamp(mix, 0, 1), t, 0.2);
    if (spec.interval !== this.semitones) {
      this.semitones = spec.interval;
      if (this.stretch) void this.stretch.schedule({ semitones: spec.interval });
      const p = this.worklet?.parameters.get('semitones');
      if (p) targetTau(p, spec.interval, t, 0.05);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.stretch) { void this.stretch.stop(); }
    try { this.worklet?.port.postMessage({ type: 'stop' }); } catch { /* gone */ }
    for (const n of [this.input, this.feedback, this.output, this.hp, this.lp, this.shifted, this.comp, this.stretch, this.worklet]) {
      try { n?.disconnect(); } catch { /* gone */ }
    }
  }
}
